import InfoIcon from "@mui/icons-material/Info";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { scaleLinear, scaleTime, timeMonth } from "d3";
import { useState } from "react";
import { buildEndpoint } from "../../services/api.service";
import { userIsAdmin } from "../../utils/UserInfoHandler";
import { TimeAxis } from "../components/axis/timeAxis";
import { YAxis } from "../components/axis/yAxis";
import { DottedLinesRenderer } from "../components/renderers/dottedLinesRenderer";
import { StaticLegends } from "../components/staticLegends";
import { Line } from "../types";
import { BarForEachMonth } from "./BarForEachMonth";
import { getFirstDayOfMonth, maximos } from "./maximos";

export function HistoricoFaturacaoChart() {
	const [processedData, setProcessedData] = useState<Line | null>(null);
	const startDate = getFirstDayOfMonth(1);
	const endDate = getFirstDayOfMonth(12);
	const MARGINS = { top: 20, bottom: 50, left: 100, right: 50 };
	const yMax: number = 140000;

	const currentYearString = new Date().getFullYear().toString();

	const svgHeight = 400;
	const [width, setWidth] = useState(600);

	useQuery({
		queryKey: ["faturacao-total"],
		queryFn: async () => (await axios.get(buildEndpoint(`/dashboard/faturacao-total?startDate=${currentYearString}-01-01&endDate=${currentYearString}-12-31`))).data,
		onSuccess: (data) => {
			const allDates = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
			const pData = allDates.map((ad, idx) => ({
				x: getFirstDayOfMonth(ad),
				y: data[idx] ? data[idx].totalvalue / 1.23 : 0,
			}));
			setProcessedData({ dataPoints: pData, metadata: { label: !userIsAdmin() ? currentYearString : `${currentYearString} (Total ${pData.reduce((acc, curr) => acc + curr.y, 0).toLocaleString("pt", { style: "currency", currency: "EUR" })})`, color: "#f57e42" } });
		},
	});

	const tScale = scaleTime()
		.domain([startDate.getTime(), endDate.getTime()])
		.range([MARGINS.left, width - MARGINS.right])
		.nice(timeMonth);

	const yScale = scaleLinear()
		.domain([yMax, 0])
		.range([MARGINS.top, svgHeight - MARGINS.bottom]);

	const xAxisTickFormatter = (tickVal: any) => {
		const monthStrings = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
		const date = new Date(tickVal);
		return monthStrings[date.getMonth()];
	};

	const staticData: Line = {
		dataPoints: maximos,
		metadata: {
			label: userIsAdmin() ? `Melhores (Total: ${maximos.reduce((acc, curr) => acc + curr.y, 0).toLocaleString("pt", { style: "currency", currency: "EUR" })})` : `Melhores`,
			color: "#4287f5",
		},
	};

	if (!processedData) return null;

	return (
		<div className="flex">
			<div className=" border rounded-xl shadow-md w-[600px] mr-10">
				<div className="bg-red-300 p-2 rounded-t-xl flex items-center justify-between">
					<h1 className="font-bold pl-5 pb-1">{"Objetivos de faturação"}</h1>
					<InfoIcon
						className="ml-2 text-blue-500"
						style={{
							fontSize: "1.3em",
						}}
					/>
				</div>

				<div className="w-full items-center">
					<svg id={"chart-container"} width={width} height={svgHeight}>
						<YAxis margins={MARGINS} yScale={yScale} chartDimensions={{ height: svgHeight, width: width }} tickFormattingFunction={(tickVal: number) => tickVal.toString()} />
						<TimeAxis margins={MARGINS} xScale={tScale} yPosition={svgHeight - MARGINS.bottom} tickFormattingFunction={xAxisTickFormatter} />
						<DottedLinesRenderer xScale={tScale} yScale={yScale} data={[staticData, processedData]} />
					</svg>
					<StaticLegends filteredLabels={[]} setFilteredLabels={() => {}} plots={[staticData, processedData]?.map((d: Line) => d?.metadata) || []} />
				</div>
			</div>
			<div className="flex w-[500px] ">
				<BarForEachMonth currentBests={staticData} currentYear={processedData} />
			</div>
		</div>
	);
}
